import { Paper, Title, Text, Flex } from "@mantine/core";
import { IconHeart, IconClock, IconBrain } from "@tabler/icons-react";

interface LazyEyeWhatIsCardProps {
  patientAge?: number;
}

export function LazyEyeWhatIsCard({ patientAge }: LazyEyeWhatIsCardProps = {}) {
  const isAdult = typeof patientAge === "number" && patientAge >= 18;

  return (
    <Paper shadow="md" mb="sm" p="sm" withBorder radius="md">
      <Title order={3} mb="xs">
        What is Lazy Eye?
      </Title>

      <Text c="dimmed" mb="sm">
        Lazy eye (amblyopia) happens when one eye doesn't develop normal vision, usually starting
        early in life. The brain and the affected eye don't fully learn to work together, so the
        brain begins to rely more on the stronger eye.
      </Text>

      <Flex gap="md" wrap="wrap" className="print-row-3">
        <Paper
          p="md"
          withBorder
          radius="md"
          style={{
            flex: 1,
            backgroundColor: "var(--oculera-tint-red)",
            borderColor: "var(--oculera-tint-red-border)",
          }}
        >
          <IconHeart
            size={24}
            color="var(--mantine-color-red-6)"
            style={{ marginBottom: "0.5rem" }}
          />
          <Title order={4} mb="xs" c="red.8">
            Common & Treatable
          </Title>
          <Text size="sm" c="dimmed">
            About 3 in 100 people develop lazy eye. The good news is that it's one of the more
            treatable visual conditions, especially when identified early.
          </Text>
        </Paper>

        <Paper
          p="md"
          withBorder
          radius="md"
          style={{
            flex: 1,
            backgroundColor: "var(--oculera-tint-blue)",
            borderColor: "var(--oculera-tint-blue-border)",
          }}
        >
          <IconClock
            size={24}
            color="var(--mantine-color-blue-6)"
            style={{ marginBottom: "0.5rem" }}
          />
          <Title order={4} mb="xs" c="blue.8">
            {isAdult ? "Improvement Is Still Possible" : "Early is Better"}
          </Title>
          <Text size="sm" c="dimmed">
            {isAdult
              ? "Even in adulthood, the visual system can respond to targeted treatment. While outcomes are often strongest when intervention begins early, structured therapy can still produce meaningful improvement."
              : "The earlier amblyopia is identified, the more responsive it tends to be to treatment. Outcomes are usually best when intervention begins in childhood, but improvement is still possible in adulthood."}
          </Text>
        </Paper>

        <Paper
          p="md"
          withBorder
          radius="md"
          style={{
            flex: 1,
            backgroundColor: "var(--oculera-tint-green)",
            borderColor: "var(--oculera-tint-green-border)",
          }}
        >
          <IconBrain
            size={24}
            color="var(--mantine-color-green-6)"
            style={{ marginBottom: "0.5rem" }}
          />
          <Title order={4} mb="xs" c="green.8">
            Brain Training
          </Title>
          <Text size="sm" c="dimmed">
            Treatment helps train the brain to use both eyes properly. It might include glasses, eye
            patches, or special exercises.
          </Text>
        </Paper>
      </Flex>
    </Paper>
  );
}
